import {
    OpenTabItemChangeQuantityDocument,
    OpenTabItemRemoveDocument,
} from '@/features/openTabs/itemMutations'
import { logger } from '@/features/openTabs/logger'
import { useHandleErrorWithToast } from '@/hooks/useHandleErrorWithToast'
import { useMutation } from '@apollo/client'
import { useCallback, useRef, useState } from 'react'

export interface SubmittedOpenTabItem {
    id: string
    /** Remaining unpaid quantity shown in the account detail. */
    quantity: number
    settledQuantity: number
}

interface UseSubmittedOpenTabItemActionsOptions {
    accountId?: string
    isOpen: boolean
    onSuccess?: () => void
    refreshAccount: () => Promise<void>
}

export const useSubmittedOpenTabItemActions = ({
    accountId,
    isOpen,
    onSuccess,
    refreshAccount,
}: UseSubmittedOpenTabItemActionsOptions) => {
    const handleError = useHandleErrorWithToast(logger)
    const [changeItemQuantity] = useMutation(OpenTabItemChangeQuantityDocument)
    const [removeOpenTabItem] = useMutation(OpenTabItemRemoveDocument)
    const [busyItemId, setBusyItemId] = useState<string>()
    const [refreshFailed, setRefreshFailed] = useState(false)
    const inFlightRef = useRef(false)
    const refreshRequiredRef = useRef(false)
    const pendingSuccessAccountIdRef = useRef<string | undefined>(undefined)
    const currentAccountIdRef = useRef(accountId)
    currentAccountIdRef.current = accountId

    const refreshAccounts = useCallback(async (): Promise<boolean> => {
        try {
            await refreshAccount()
            refreshRequiredRef.current = false
            setRefreshFailed(false)
            if (
                pendingSuccessAccountIdRef.current &&
                pendingSuccessAccountIdRef.current === currentAccountIdRef.current
            ) {
                onSuccess?.()
            }
            pendingSuccessAccountIdRef.current = undefined
            return true
        } catch {
            setRefreshFailed(true)
            return false
        }
    }, [refreshAccount, onSuccess])

    const editItem = useCallback(
        async (item: SubmittedOpenTabItem, quantityChange?: -1 | 1): Promise<boolean> => {
            if (
                !accountId ||
                !isOpen ||
                inFlightRef.current ||
                refreshRequiredRef.current ||
                !Number.isInteger(item.quantity) ||
                item.quantity <= 0 ||
                !Number.isInteger(item.settledQuantity) ||
                item.settledQuantity < 0 ||
                (quantityChange === undefined && item.settledQuantity > 0)
            ) {
                return false
            }

            inFlightRef.current = true
            refreshRequiredRef.current = true
            setBusyItemId(item.id)
            let updated = false

            try {
                if (quantityChange === undefined) {
                    const result = await removeOpenTabItem({
                        variables: { openTabUuid: accountId, itemUuid: item.id },
                    })
                    updated = result.data?.openTabItemRemove.uuid === accountId
                } else {
                    const result = await changeItemQuantity({
                        variables: {
                            openTabUuid: accountId,
                            input: { itemUuid: item.id, quantityChange },
                        },
                    })
                    updated = result.data?.openTabItemChangeQuantity.uuid === accountId
                }

                if (!updated) throw new Error('Open tab item update returned no matching account.')
                pendingSuccessAccountIdRef.current = accountId
            } catch (error) {
                void handleError(error)
            }

            try {
                // A lost mutation response may still mean the server changed the item.
                // Reload both balances and quantities before accepting another action.
                await refreshAccounts()
            } finally {
                inFlightRef.current = false
                setBusyItemId(undefined)
            }

            return updated
        },
        [accountId, changeItemQuantity, handleError, isOpen, refreshAccounts, removeOpenTabItem]
    )

    const retryRefresh = useCallback(async (): Promise<boolean> => {
        if (inFlightRef.current || !refreshRequiredRef.current) return false

        inFlightRef.current = true
        try {
            return await refreshAccounts()
        } finally {
            inFlightRef.current = false
        }
    }, [refreshAccounts])

    const changeQuantity = useCallback(
        (item: SubmittedOpenTabItem, quantityChange: -1 | 1) => editItem(item, quantityChange),
        [editItem]
    )
    const removeItem = useCallback((item: SubmittedOpenTabItem) => editItem(item), [editItem])

    return {
        busyItemId,
        saving: busyItemId !== undefined || refreshFailed,
        refreshFailed,
        retryRefresh,
        changeQuantity,
        removeItem,
    }
}
